'use client';
import { useRef, useState } from "react";
import Link from "next/link";
import { Mail, CheckCircle, CalendarDays } from "lucide-react";
import SocialMedia from "@components/socialComp";
import { track } from "@lib/track";

const COMPONENT = "ContactPageClient";

type FormData = {
  name: string;
  email: string;
  phone: string;
  message: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

export default function ContactPageClient() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  // Honeypot. Sent to the server, which makes the real decision — there is
  // deliberately no client-side early return on it: that branch on the
  // newsletter form showed no error, no success and sent no request, so a
  // password manager or iOS Contact autofill turned the button into a dead
  // control with no trace on either side.
  const [hp, setHp] = useState("");
  const mountedAt = useRef(Date.now()); // form-fill time, for the server's timing check
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [serverError, setServerError] = useState("");

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!formData.name.trim()) errs.name = "Name is required.";
    // Trim before validating. The regex rejects surrounding whitespace, and
    // accepting an autocomplete suggestion on iOS/Android appends a trailing
    // space — so an address that was perfectly valid got "Please enter a valid
    // email address" and never reached the API, while the server (which trims)
    // would have taken it happily. Same bug fixed on /api/subscribe 2026-09-12.
    if (!formData.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      errs.email = "Please enter a valid email address.";
    if (!formData.message.trim()) errs.message = "Please tell us how we can help.";
    return errs;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();

    // Attempt, not success — fires on every press of Send, before the client
    // validation gate. `valid` is what makes a form that refuses good input
    // visible in the data: a run of contact_submit with valid:false and no
    // contact_sent is the form rejecting people, not people losing interest.
    // page_view (label "/contactus", from PageViewTracker in the root layout)
    // is the step above this one and joins on the same label.
    try {
      track(
        "contact_submit",
        { label: "/contactus", component: COMPONENT },
        {
          event: "contact_submit",
          label: "/contactus",
          component: COMPONENT,
          meta: { valid: Object.keys(errs).length === 0, hasPhone: !!formData.phone.trim() },
        }
      );
    } catch {}

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setStatus("loading");
    setServerError("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          message: formData.message.trim(),
          hp,
          elapsedMs: Date.now() - mountedAt.current,
        }),
      });

      const data = await response.json().catch(() => ({}));

      // A rate-limited sender is a real person who needs to know why and what
      // to do instead — the server sends that wording, so use it rather than
      // the generic failure. (A honeypot/timing rejection never lands here: it
      // returns 200 and is indistinguishable from success, by design.)
      if (!response.ok) throw new Error(data.message || "Failed to send");

      // Success — fires after the API confirms, never on the error path. This
      // is the terminating event of the contact funnel.
      try {
        track(
          "contact_sent",
          { label: "/contactus", component: COMPONENT },
          { event: "contact_sent", label: "/contactus", component: COMPONENT }
        );
      } catch {}

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setServerError(
        err instanceof Error && err.message
          ? err.message
          : "Something went wrong. Please try again or email us directly."
      );
    }
  };

  if (status === "success") {
    return (
      <main
        id="main-content"
        className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-16 text-center"
      >
        <CheckCircle className="w-16 h-16 text-[var(--color-success)] mb-6" />
        <h1 className="text-2xl md:text-3xl font-semibold text-[var(--color-text-brand-contrast)] mb-3">
          Message received!
        </h1>
        <p className="text-slate-600 max-w-md">
          Thank you for getting in touch. We&apos;ll get back to you within
          24–48 hours. A confirmation has been sent to{" "}
          <strong>{formData.email}</strong>.
        </p>
      </main>
    );
  }

  return (
    <main id="main-content" className="bg-[var(--bg-surface)]">
      {/* Page header */}
      <div className="bg-[var(--color-brand)]/5 py-8 md:py-12 text-center px-4">
        <h1 className="text-3xl md:text-4xl font-semibold text-[var(--color-text-brand-contrast)] mb-3">
          Get in touch
        </h1>
        <p className="text-slate-600 max-w-xl mx-auto text-sm md:text-base">
          Whether you have a question, want to book a session, or are interested
          in room hire — we&apos;d love to hear from you.
        </p>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-8 md:py-12 space-y-10 md:space-y-14">
        {/* ── Booking shortcuts ── */}
        <section aria-label="Book a discovery call">
          <div className="flex justify-center">
            <div className="flex flex-col rounded-2xl border border-[var(--color-brand)]/20 bg-[var(--color-brand)]/5 p-5 gap-3 w-full max-w-md">
              <div className="flex items-center gap-3">
                <CalendarDays className="w-6 h-6 shrink-0 text-[var(--color-brand)]" />
                <h3 className="font-semibold text-[var(--color-text-brand-contrast)]">
                  Free discovery call
                </h3>
              </div>
              <p className="text-sm text-slate-600">
                Not sure where to start? Book a free 15-minute call with Niki.
                No pressure, no obligation — just a chance to talk through your
                goals.
              </p>
              <Link
                href="/workwithme/discoverycall"
                className="mt-1 inline-flex items-center justify-center rounded-xl bg-[var(--color-brand)] px-5 py-3 text-sm font-semibold text-white shadow hover:bg-[var(--color-brand)]/80 focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] transition"
              >
                Book a free discovery call
              </Link>
            </div>
          </div>
        </section>

        {/* ── Divider ── */}
        <div className="flex items-center gap-4">
          <div className="flex-1 border-t border-slate-200" />
          <span className="text-sm text-slate-400 whitespace-nowrap">
            or send us a message
          </span>
          <div className="flex-1 border-t border-slate-200" />
        </div>

        {/* ── Two-column: info + form ── */}
        <div className="grid md:grid-cols-2 gap-10 md:gap-12">
          {/* Contact info */}
          <div className="flex flex-col gap-6 md:gap-8">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-text-brand-contrast)] mb-2">
                Email us directly
              </h2>
              <a
                href="mailto:enquiries@cforganicwellness.com"
                className="inline-flex items-center gap-2 text-[var(--color-brand)] hover:underline font-medium text-sm md:text-base break-all"
              >
                <Mail className="w-5 h-5 shrink-0" />
                enquiries@cforganicwellness.com
              </a>
            </div>

            <div>
              <h2 className="text-base font-semibold text-[var(--color-text-brand-contrast)] mb-2">
                Response time
              </h2>
              <p className="text-slate-600 text-sm md:text-base">
                We aim to respond to all enquiries within 24–48 hours.
              </p>
            </div>

            <div>
              <h2 className="text-base font-semibold text-[var(--color-text-brand-contrast)] mb-3">
                Follow us
              </h2>
              <SocialMedia />
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="flex flex-col gap-5"
          >
            {/* Honeypot. Positioned off-screen rather than display:none — a bot that
                parses the DOM skips hidden fields, but fills visible-to-CSS ones.
                Named so a form-filler wants to complete it; autoComplete off so a
                real browser never does. Same field name as the newsletter form. */}
            <input
              type="text"
              name="contact_website"
              tabIndex={-1}
              autoComplete="off"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
              aria-hidden="true"
            />

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Name <span aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                autoComplete="name"
                required
                value={formData.name}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-3 text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] ${
                  errors.name ? "border-red-400" : "border-slate-300"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Email <span aria-hidden="true">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-3 text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] ${
                  errors.email ? "border-red-400" : "border-slate-300"
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Phone{" "}
                <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-3 text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                How can we help? <span aria-hidden="true">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                value={formData.message}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-3 text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] resize-none ${
                  errors.message ? "border-red-400" : "border-slate-300"
                }`}
              />
              {errors.message && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {errors.message}
                </p>
              )}
            </div>

            {serverError && (
              <p className="text-sm text-red-600" role="alert">
                {serverError}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full rounded-xl bg-[var(--color-brand)] px-6 py-3.5 font-semibold text-white shadow hover:bg-[var(--color-brand)]/80 focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] disabled:opacity-60 transition"
            >
              {status === "loading" ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
